import React from "react";
import { Music, Camera, Video, Paintbrush, Calendar, MapPin, Clock, CheckCircle2, Layers } from "lucide-react";

const brl = (n) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);

const STATUS = {
  pending: { label: "Aguardando pagamento", cls: "bg-[#FFCC00]/15 text-[#FFCC00] border-[#FFCC00]/40" },
  paid: { label: "PAGA", cls: "bg-[#5AF78E]/15 text-[#5AF78E] border-[#5AF78E]/40" },
  rejected: { label: "Rejeitada", cls: "bg-[#C9A84C]/15 text-[#C9A84C] border-[#C9A84C]/40" },
};

const SERVICES = [
  { key: "musica", label: "Música", Icon: Music },
  { key: "foto", label: "Foto", Icon: Camera },
  { key: "video", label: "Vídeo", Icon: Video },
  { key: "pintura", label: "Pintura", Icon: Paintbrush },
];

export const dobrasOf = (r) =>
  r.dobra_category_ids && r.dobra_category_ids.length > 0
    ? r.dobra_category_ids
    : r.dobra_category_id
    ? [r.dobra_category_id]
    : [];

export const RegistrationCard = ({ r, comp, catNames, schedule }) => {
  const s = STATUS[r.status] || STATUS.pending;
  const isPaid = r.status === "paid";
  const dobras = dobrasOf(r);
  const mainName = catNames[r.category_id] || "Categoria";
  const timeOf = (catId) => schedule?.[catNames[catId]] || null;
  const services = SERVICES.filter((sv) => r.servicos?.[sv.key]);

  return (
    <div data-testid={`my-reg-${r.id}`} className={"card overflow-hidden border-l-4 " + (isPaid ? "border-[#5AF78E]" : "border-[#FFCC00]")}>
      <div className="p-5 pb-4 flex items-start justify-between gap-4 border-b border-white/10 bg-black/30">
        <div>
          <div className="overline mb-1">Número de chamada</div>
          {r.numero ? (
            <div data-testid={`my-reg-numero-${r.id}`} className="font-display font-black text-6xl leading-none text-[#C9A84C] tabular-nums">
              #{r.numero}
            </div>
          ) : (
            <div data-testid={`my-reg-numero-${r.id}`} className="text-sm text-[var(--text-secondary)] italic mt-2">
              Número ainda não definido
            </div>
          )}
        </div>
        <div className="text-right min-w-0">
          <div className="overline mb-1">Competição</div>
          <div className="font-display text-xl font-black tracking-tight leading-tight truncate">{comp?.nome || "Competição"}</div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div>
          <div className="overline mb-1">Categoria principal</div>
          <div className="flex items-center justify-between gap-3">
            <div data-testid={`my-reg-main-cat-${r.id}`} className="font-display font-black text-2xl tracking-tight text-white">{mainName}</div>
            {timeOf(r.category_id) && (
              <span className="text-xs text-[#C9A84C] tabular-nums inline-flex items-center gap-1 whitespace-nowrap">
                <Clock size={12} /> {timeOf(r.category_id)}
              </span>
            )}
          </div>
        </div>

        {dobras.length > 0 && (
          <div>
            <div className="overline mb-1 flex items-center gap-1"><Layers size={11} /> Dobras ({dobras.length})</div>
            <ul className="space-y-1" data-testid={`my-reg-dobras-${r.id}`}>
              {dobras.map((d, i) => (
                <li key={d || i} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 py-1">
                  <span><span className="chip chip-judge mr-2">DOBRA {i + 1}</span>{catNames[d] || d}</span>
                  {timeOf(d) && <span className="text-xs text-[var(--text-secondary)] tabular-nums inline-flex items-center gap-1"><Clock size={11} /> {timeOf(d)}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {services.length > 0 && (
          <div>
            <div className="overline mb-2">Serviços adicionais</div>
            <div className="flex flex-wrap gap-2" data-testid={`my-reg-services-${r.id}`}>
              {services.map(({ key, label, Icon }) => (
                <span
                  key={key}
                  data-testid={`my-reg-service-${key}-${r.id}`}
                  className={"inline-flex items-center gap-1.5 border px-2 py-1 text-xs " + (isPaid ? "border-[#5AF78E]/40 text-[#5AF78E] bg-[#5AF78E]/10" : "border-[#FFCC00]/40 text-[#FFCC00] bg-[#FFCC00]/10")}
                >
                  <Icon size={12} /> {label}
                  <span className="text-[9px] uppercase tracking-widest font-bold opacity-80">· {isPaid ? "Confirmado" : "Pendente"}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-xs text-[var(--text-secondary)] border-t border-white/10 pt-3">
          <span className="inline-flex items-center gap-1.5"><Calendar size={12} /> {comp?.data || "—"}</span>
          <span className="inline-flex items-center gap-1.5 truncate"><MapPin size={12} /> {comp?.local || "Local a definir"}</span>
        </div>
      </div>

      <div className="px-5 py-4 bg-black/40 border-t border-white/10 text-sm space-y-1.5">
        <div className="flex justify-between"><span className="text-[var(--text-secondary)]">Inscrição{r.lote_nome ? ` · ${r.lote_nome}` : ""}</span><span className="tabular-nums">{brl(r.valor_inscricao)}</span></div>
        <div className="flex justify-between"><span className="text-[var(--text-secondary)]">Dobras ({dobras.length})</span><span className="tabular-nums">{brl(r.valor_dobra)}</span></div>
        <div className="flex justify-between"><span className="text-[var(--text-secondary)]">Serviços</span><span className="tabular-nums">{brl(0)}</span></div>
        <div className="flex items-center justify-between border-t border-white/10 pt-2 mt-2">
          <div>
            <div className="overline">{isPaid ? "Total pago" : "Total a pagar"}</div>
            <div data-testid={`my-reg-total-${r.id}`} className="font-display font-black text-3xl text-[#C9A84C] tabular-nums leading-none">{brl(r.valor_total)}</div>
          </div>
          <span data-testid={`my-reg-status-${r.id}`} className={"border px-2.5 py-1 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1.5 whitespace-nowrap " + s.cls}>
            {isPaid && <CheckCircle2 size={12} />}{s.label}
          </span>
        </div>
      </div>
    </div>
  );
};
